import { type ReactNode } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { tokenRequest } from "./msalConfig";
import { BrandedLoader } from "@/components/BrandedLoader";
import gwLogoUrl from "@/assets/gw-logo.svg";

/**
 * Renders children when an account is signed in. Otherwise shows a branded sign-in card with
 * the GorillaWorks logo, app title, and a Microsoft-branded sign-in button that kicks off the
 * redirect flow. When MSAL is disabled (dev/stub mode), `main.tsx` skips wrapping with
 * MsalProvider — AuthGate is never mounted in that case.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();

  // While MSAL is mid-interaction (startup, redirect handshake, login, token acquisition) neither
  // template should render — show the branded loader instead of flashing the sign-in card.
  if (inProgress !== InteractionStatus.None) {
    return <BrandedLoader variant="fullscreen" label="Signing in…" />;
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="auth-screen">
          <main className="auth-card" role="dialog" aria-labelledby="auth-title">
            <img src={gwLogoUrl} alt="" className="auth-logo" aria-hidden="true" />
            <h1 id="auth-title" className="auth-title">
              Onboarding Analytics
            </h1>
            <p className="auth-subtitle">Sign in with your organization account to continue.</p>
            <button
              type="button"
              className="auth-ms-btn"
              onClick={() => instance.loginRedirect(tokenRequest)}
            >
              <MicrosoftMark />
              <span>Sign in with Microsoft</span>
            </button>
            <footer className="auth-footer">
              <span className="auth-footer-brand">GorillaWorks</span>
              <span className="auth-footer-sep">·</span>
              <span>Onboarding Analytics</span>
            </footer>
          </main>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

/** The official Microsoft 4-square mark used on "Sign in with Microsoft" buttons. */
function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
